// Hulp- en uitlegpagina voor de eindgebruiker. Open via de "?"-knop in de
// topbar. Inhoud volgt de structuur die in de README ook staat, maar in
// een formaat dat tijdens het gebruik direct beschikbaar is.

import { Modal } from "./ui/Modal";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function HelpDialog({ open, onClose }: Props) {
  return (
    <Modal open={open} onClose={onClose} title="Uitleg en help" width="42rem">
      <div className="prose-sm space-y-6 text-sm leading-relaxed">
        <Section title="Wat doet deze tool?">
          <p>
            De Schaduwtool helpt bewoners, raadsleden, juristen en
            belangengroepen om zelfstandig een beeld te vormen van de
            schaduwwerking van een (gepland) gebouw op nabijgelegen tuinen
            en daken (zonnepanelen). Je tekent zelf de bouwblokken,
            omliggende tuinen en eventueel bomen op een luchtfoto van de
            betreffende locatie. De tool berekent vervolgens — op basis van
            de zonnestand — waar en wanneer schaduw valt.
          </p>
          <p>
            De berekening is <strong>indicatief en geometrisch</strong>:
            er wordt geen rekening gehouden met bewolking, reflectie of
            atmosferische verstrooiing. Het doel is een onderbouwd gesprek
            mogelijk te maken — niet om als juridisch bewijs te dienen.
          </p>
        </Section>

        <Section title="Hoe teken ik blokken?">
          <p>
            In het linkerpaneel staat een werkbalk met de teken-tools.
            Kies eerst het <em>type</em> blok (Gebouw, Tuin, Bestrating,
            Boomstam of Boombladdek). Daarna een tool:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Polygoon</strong> — klik per hoekpunt op de kaart;
              sluit met <kbd>Enter</kbd>, dubbelklik, of klik dichtbij het
              eerste punt.
            </li>
            <li>
              <strong>Rechthoek</strong> — klik twee tegenoverliggende
              hoeken.
            </li>
            <li>
              <strong>Punt verplaatsen</strong> — sleep een hoekpunt van
              het geselecteerde blok.
            </li>
            <li>
              <strong>Blok verslepen</strong> — sleep het hele
              geselecteerde blok.
            </li>
            <li>
              <strong>Verwijderen</strong> — klik op een blok om het te
              verwijderen.
            </li>
            <li>
              <strong>Undo / Redo</strong> — knoppen of{" "}
              <kbd>Ctrl</kbd>+<kbd>Z</kbd> / <kbd>Ctrl</kbd>+
              <kbd>Shift</kbd>+<kbd>Z</kbd>.
            </li>
          </ul>
          <p>
            Hoekpunten <em>snappen</em> automatisch aan bestaande
            hoekpunten in de buurt (binnen ±12 pixels). <kbd>Esc</kbd>{" "}
            annuleert de teken-modus. Selecteer een blok om in het
            rechterpaneel hoogte, kleur en transparantie aan te passen.
          </p>
        </Section>

        <Section title="Datum- en tijdslider">
          <p>
            De onderbalk bevat twee sliders.{" "}
            <strong>Schaduw wordt alleen getekend wanneer de zon
            ≥ 10° boven de horizon staat</strong> (de Haagse-norm-conventie:
            onder dat niveau wordt de zon effectief geblokkeerd door
            horizonbebouwing).
          </p>
          <h4 className="font-semibold mt-3">Datumslider</h4>
          <p>
            Loopt van 1 januari tot 31 december van het referentiejaar
            (2025; verschillen per jaar zijn voor zonnestand
            verwaarloosbaar). De gekleurde markeringen springen direct
            naar belangrijke data:
          </p>
          <ul className="list-none pl-0 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="w-3 h-3 rounded-sm bg-emerald-500 mt-1 flex-none" />
              <span>
                <strong>21 maart, 21 juni, 21 september, 21 december</strong>
                {" "}
                — equinoxen en zonnewendes. Op de equinoxen duren dag en
                nacht ongeveer even lang; 21 juni is de langste dag, 21
                december de kortste.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-3 h-3 rounded-sm bg-amber-500 mt-1 flex-none" />
              <span>
                <strong>19 februari + 21 oktober</strong> — toetsdatums voor
                de <em>lichte</em> Haagse bezonningsnorm.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-3 h-3 rounded-sm bg-rose-600 mt-1 flex-none" />
              <span>
                <strong>19 januari + 22 november</strong> — toetsdatums voor
                de <em>strenge</em> Haagse bezonningsnorm.
              </span>
            </li>
          </ul>
          <p className="mt-3">
            Naast de slider staat een tekstveld <code>DD-MM</code> waarmee
            je elke datum direct kunt invoeren.
          </p>
          <h4 className="font-semibold mt-3">Tijdslider</h4>
          <p>
            Loopt van 00:00 tot 23:50 in stappen van 10 minuten in
            Nederlandse kloktijd (inclusief zomer-/wintertijd-correctie).
            Onder de slider staat de zonsopkomst en -ondergang voor de
            gekozen datum + locatie. Rechts toont de tool de actuele
            zon-azimuth (kompasrichting) en zon-elevatie (hoogte boven
            horizon) — een rode elevatie betekent &lt; 10° en dus geen
            slagschaduw.
          </p>
        </Section>

        <Section title="Jaarstatistieken — wat betekenen de velden?">
          <p>
            Wanneer je een tuin/bestrating selecteert, of een gebouw met
            een dak, kun je in het rechterpaneel op{" "}
            <strong>"Bereken jaarschaduw"</strong> klikken. Een Web Worker
            sampelt vervolgens elke 15 minuten van het hele jaar (alleen
            tijdens daglicht), bepaalt voor elke 0,5 m × 0,5 m cel op het
            meetvlak of die in schaduw lag, en aggregeert het resultaat:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong>Totaal schaduwuren / jaar</strong> — het aantal uren
              waarin een gemiddelde cel op dit meetvlak in schaduw lag,
              gesommeerd over een heel jaar.
            </li>
            <li>
              <strong>Aandeel van daglichturen</strong> — hetzelfde getal
              uitgedrukt als percentage van de totale daglichturen op deze
              breedtegraad.
            </li>
            <li>
              <strong>Daglichturen totaal</strong> — het aantal uren in
              het jaar waarop de zon ≥ 10° boven de horizon staat. Voor
              Hilversum is dat ≈ 4 100 uur per jaar.
            </li>
            <li>
              <strong>Cellen op meetvlak</strong> — het aantal 0,5 m × 0,5
              m cellen waarin de polygoon van het meetvlak verdeeld is.
              Hoe groter het meetvlak, hoe meer cellen.
            </li>
          </ul>
        </Section>

        <Section title="Wat betekent “Verdeling”?">
          <p>
            Niet elke cel op een meetvlak heeft evenveel schaduw — een
            stukje tuin pal naast een hoge muur ligt vaak in schaduw,
            terwijl het andere eind in volle zon ligt. De{" "}
            <strong>verdeling</strong> vertelt hoe het meetvlak ruimtelijk
            opgesplitst is naar schaduwgraad. Elke cel valt in één van vijf
            buckets, op basis van wat haar individuele schaduwfractie over
            het hele jaar is:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>0%</strong> — vrijwel geen schaduw (&lt; 0,5% van de
              daglichturen).
            </li>
            <li>
              <strong>0–33%</strong> — een beetje schaduw.
            </li>
            <li>
              <strong>33–66%</strong> — gemiddeld evenveel zon als schaduw.
            </li>
            <li>
              <strong>66–100%</strong> — overwegend schaduw.
            </li>
            <li>
              <strong>100%</strong> — vrijwel altijd schaduw (≥ 99,5%).
            </li>
          </ul>
          <p>
            Het percentage achter de balk is dus het{" "}
            <em>aandeel cellen</em> in die bucket, niet de schaduwgraad
            zelf. Een tuin met "60% van de cellen in 0%-bucket en 40% in
            66–100%-bucket" is waarschijnlijk grofweg in tweeën gesneden
            door een gebouwschaduw.
          </p>
        </Section>

        <Section title="Wat betekent “Per maand”?">
          <p>
            De staafjes onder "Per maand" tonen de gemiddelde
            schaduwfractie per kalendermaand, gemeten over alle cellen en
            daglicht-samples in die maand. Hogere staafjes = meer schaduw.
          </p>
          <p>
            In Nederland zie je doorgaans hogere balken in de
            wintermaanden: de zon staat lager, schaduwen zijn langer en
            dekken een groter deel van het meetvlak. In juni–juli staat de
            zon hoog en zijn schaduwen kort.
          </p>
          <p>
            De hoogte is genormaliseerd t.o.v. de hoogste maand zodat het
            patroon goed afleesbaar is, ook als alle waardes klein zijn.
            De exacte percentages zie je in de tooltip bij hover.
          </p>
        </Section>

        <Section title="De Haagse bezonningsnorm">
          <p>
            De Haagse bezonningsnorm is een gangbare toets in
            ruimtelijke-ordening-procedures: hij stelt minimum-eisen aan
            de hoeveelheid zonlicht die een woning of tuin moet krijgen op
            specifieke "ongunstige" datums.
          </p>
          <ul className="list-none pl-0 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="w-3 h-3 rounded-sm bg-amber-500 mt-1 flex-none" />
              <span>
                <strong>Lichte toets</strong> — op{" "}
                <strong>19 februari</strong> én{" "}
                <strong>21 oktober</strong> moet er minimaal{" "}
                <strong>2 uur ononderbroken zon</strong> zijn op een
                kritiek meetpunt, tussen 10:00 en 18:00.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-3 h-3 rounded-sm bg-rose-600 mt-1 flex-none" />
              <span>
                <strong>Strenge toets</strong> — op{" "}
                <strong>19 januari</strong> én{" "}
                <strong>22 november</strong> moet er minimaal{" "}
                <strong>3 uur ononderbroken zon</strong> zijn, in
                hetzelfde tijdsvenster. Op deze datums staat de zon nog
                lager dan in februari/oktober.
              </span>
            </li>
          </ul>
          <p>
            De knop <em>"Toets aan Haagse norm"</em> in het
            resultaten-paneel rekent dit per geselecteerd blok uit. Als
            kritiek meetpunt wordt het zwaartepunt van het meetvlak
            gebruikt — een professioneel bureau zou meerdere
            (worst-case) punten meenemen, dus voor formele toetsing is
            een onafhankelijk rapport vereist.
          </p>
          <p className="text-xs text-muted-foreground">
            Achtergrond: <em>"Bezonningsnorm Haagse Hofkwaliteit"</em>,
            opgenomen in beleidsregels van de gemeente Den Haag en
            inmiddels door verschillende andere gemeenten als
            toetsingskader overgenomen. De norm gaat oorspronkelijk uit
            van een meetpunt op een gevel; voor tuinen wordt vaak het
            gebruikersvriendelijkere zwaartepunt-meetpunt gehanteerd.
          </p>
        </Section>

        <Section title="Beperkingen en disclaimer">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              Alleen <strong>geometrische schaduw</strong> — geen
              bewolking, reflectie van glas/water, of atmosferische
              verstrooiing.
            </li>
            <li>
              Daken zijn <strong>plat en horizontaal</strong>; schuine
              daken worden in v1 nog niet correct gemodelleerd.
            </li>
            <li>
              Bestaande bomen en gebouwen tellen alleen mee als je ze
              zélf intekent. Een lege omgeving rondom je bouwplan
              onderschat de werkelijke schaduw.
            </li>
            <li>
              Bomen worden gemodelleerd als één blok met een
              "lichtdoorlatendheid" (default: 40% voor een bladdek).
              Seizoensvariatie (ontblaadering) wordt niet meegenomen.
            </li>
            <li>
              <strong>Tijdzone</strong>: alle invoer en weergave is in
              Nederlandse kloktijd (inclusief zomer-/wintertijd).
            </li>
            <li>
              <strong>Privacy</strong>: deze tool verzamelt geen
              persoonsgegevens behalve een admin-username (alleen voor
              beheerders). Persoonlijke scenario's blijven in de browser
              (IndexedDB) en gaan nooit naar de server.
            </li>
            <li>
              <strong>Voor juridische procedures</strong> is een
              onafhankelijk rapport van een gecertificeerd bureau
              noodzakelijk. Deze tool levert daar wél de input voor: een
              reproduceerbaar scenario en concreet besprekingsmateriaal.
            </li>
          </ul>
        </Section>
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-base font-semibold mb-2 text-foreground">{title}</h3>
      <div className="text-foreground/90 space-y-2">{children}</div>
    </section>
  );
}
